import { Stack } from '@mui/material';
import React from 'react';

import { Tag } from '@/shared/components/ui/tag/tag';
import {
  TTagsListProps,
  TTagsListRenderProps,
} from '@/shared/components/ui/tags/tags.types';
import { Tooltip } from '@/shared/components/ui/tooltip/tooltip';
import { TruncatedText } from '@/shared/components/ui/truncated-text/truncated-text';

const MAX_VISIBLE_TAGS = 4;
const TAG_MAX_LENGTH = 8;

export const Tags: React.FC<TTagsListProps> = ({
  tags,
  onDelete,
  visibleTags = MAX_VISIBLE_TAGS,
  customLabel,
  disableTooltip,
  tagMaxLength = TAG_MAX_LENGTH,
  onTagClick,
  noTruncatedList,
}) => {
  if (!tags?.length) {
    return null;
  }

  const createTagLists = (): TTagsListRenderProps => {
    if (visibleTags === 0) {
      return {
        tagsToDisplay: [],
        otherTags: tags,
      };
    }

    if (tags.length - 1 === visibleTags) {
      return {
        tagsToDisplay: tags,
        otherTags: [],
      };
    }

    const tagsToDisplay = tags.slice(0, visibleTags);
    const otherTags = tags.slice(visibleTags);

    return {
      tagsToDisplay,
      otherTags,
    };
  };

  const renderLabel = (count: number) => {
    if (!customLabel) {
      return `+${count}`;
    }

    return customLabel(count);
  };

  const renderTooltip = () => {
    if (disableTooltip) {
      return '';
    }

    return (
      <Stack spacing="0.25rem" py="0.25rem">
        {otherTags.map((tag, index) => (
          <Tag
            onClick={onTagClick ? () => onTagClick?.(tag) : undefined}
            key={`${tag.name}_${index}`}
            label={tag.name}
            onDelete={onDelete ? () => onDelete?.(tag) : undefined}
          />
        ))}
      </Stack>
    );
  };

  const { tagsToDisplay, otherTags } = createTagLists();

  if (noTruncatedList) {
    return (
      <Stack direction="row" gap="0.375rem" flexWrap="wrap">
        {tags.map((tag, index) => (
          <Tag
            key={`${tag.name}-${index}`}
            onClick={onTagClick ? () => onTagClick?.(tag) : undefined}
            label={tag.name}
            onDelete={onDelete ? () => onDelete?.(tag) : undefined}
          />
        ))}
      </Stack>
    );
  }

  return (
    <Stack direction="row" gap="0.375rem">
      {tagsToDisplay.map((tag, index) => (
        <Tag
          key={`${tag.name}-${index}`}
          label={
            <TruncatedText
              color="textPrimary"
              variant="p8"
              text={tag.name}
              maxLength={tagMaxLength}
            />
          }
          onDelete={onDelete ? () => onDelete?.(tag) : undefined}
        />
      ))}
      {otherTags.length > 0 && (
        <Tooltip title={renderTooltip()} wrapper="default">
          <Tag key="other-tags" label={renderLabel(otherTags.length)} />
        </Tooltip>
      )}
    </Stack>
  );
};
